import React from 'react';
import { LocationDescriptor } from 'history';
import { MaskedInputProps } from 'react-text-mask';

export enum PasswordRuleError {
  TooShort = 'tooShort',
  TooLong = 'tooLong',
  NeedsLetter = 'needsLetter',
  NeedsNumber = 'needsNumber',
  NeedsSpecialCharacter = 'needsSpecialCharacter',
}

export type PasswordErrorReporter = (errors: Array<PasswordRuleError>) => FieldError;
export type PasswordRules = Array<(password: string) => PasswordRuleError | false>;

export type FieldError = string | false;

export type FormController = {
  submit: (e: React.FormEvent<HTMLFormElement>) => void;
  validationHandlers: Record<string, () => boolean>;
};

export type FieldWrapperProps = {
  /**
   * Display an HTML label with the input. If not populated, the label element is omitted entirely.
   */
  label?: string;

  /**
   * Help/hint text that appears below the input.
   */
  help?: string;

  /**
   * A UI element that will appear inside the input on the right side.
   * If present, will override the error icon, since that's the space it normally occupies.
   */
  inputWidget?: React.ReactElement;

  /**
   * Whether or not the input widget can be shown. Will prevent the display of the error icon as well if false.
   */
  showInputWidget?: boolean;
};

export type ControlProps = {
  /**
   * The name for the input. Just like the default HTML name property, but required.
   */
  name: string;

  /**
   * A controller to manage form validation and submission. See makeFormController in utils.
   * Allows null instead of being optional because it's necessary in most cases, and you
   * should be explicit when it isn't.
   */
  formController: FormController | null;

  /**
   * A flag indicating whether or not the control has a validation error.
   */
  hasError: boolean;
};

export type TextControlProps = React.InputHTMLAttributes<HTMLInputElement> &
  ControlProps & {
    /**
     * If present, will enable an input mask. See documention for react-text-mask.
     */
    maskOptions?: MaskedInputProps;
  };

export type NumericCodeControlProps = React.InputHTMLAttributes<HTMLInputElement> &
  ControlProps & {
    /**
     * Like the standard HTML input maxLength, but required.
     */
    maxLength: number;
  };

export type FieldProps<TControlProps> = FieldWrapperProps &
  Omit<TControlProps, 'hasError'> & {
    /**
     * If present, validate the input value and retrieve the error message, if any.
     * If it returns false, the input has no errors.
     */
    getFieldError?: (value: string) => FieldError;
  };

export type TextFieldProps = FieldProps<TextControlProps> & {
  /**
   * The type of form control to be used.
   */
  controlType?: 'code' | 'text';
};

export type NumericCodeFieldProps = FieldProps<NumericCodeControlProps>;

export type PasswordFieldProps = TextFieldProps & {
  /**
   * @default see defaultRules.
   * An array of functions that check the password for validity. Each function should
   * return a PasswordRuleError or false if no error. They are evaluated from top to
   * bottom. Note that this (and errorReporter) are unused if getFieldError is overridden.
   */
  rules?: PasswordRules;

  /**
   * @default see defaultErrorReporter
   * The reporter to use to populate the field error if any password rules
   * have failed. Note that this (and rules) are unused if getFieldError is overridden.
   */
  errorReporter?: PasswordErrorReporter;

  /**
   * The URL to go to when the forgot password link is clicked.
   * If neither this nor onForgotPasswordClick are present, the button won't appear.
   */
  toForgotPassword?: LocationDescriptor;

  /**
   * An action to perform when the "forgot password" button is clicked.
   * If neither this nor forgotPasswordUrl are present, the button won't appear.
   */
  onForgotPasswordClick?: React.MouseEventHandler;

  /**
   * @default false
   * A flag to control the display of the "show"/"hide" password button.
   */
  hasShowPasswordButton?: boolean;

  /**
   * @default "Forgot password?"
   * The label for the "forgot password" button.
   * Will only display if the onForgotPasswordClick or toForgotPassword prop is present.
   */
  forgotPasswordLabel?: string;
};

export type PhoneFieldProps = TextFieldProps;



// WEBPACK FOOTER //
// ./packages/smart-home-support/components/forms/models.ts